import { useGarden } from '../context/GardenContext'
import { usePlantCatalog } from '../context/PlantCatalogContext'

export default function ToDoList() {
  const { state } = useGarden()
  const { getPlantById } = usePlantCatalog()
  const allPlantIds = [...new Set(state.containers.flatMap(container => container.plantIds))]
  const allPlants = allPlantIds.map(id => getPlantById(id)).filter(Boolean)
  const perennials = allPlants.filter(plant => plant!.isPerennial)
  const annuals = allPlants.filter(plant => !plant!.isPerennial)
  const hasBalcony = state.containers.some(container => container.location === 'balcony')
  const regionLabel = state.region === 'OR' ? 'Oregon' : state.region === 'WA' ? 'Washington' : 'your area'

  if (allPlants.length === 0 && state.containers.length === 0) {
    return (
      <div className="ac-card text-center py-8">
        <p className="font-body text-ac-brown-light">Your care schedule will appear here once you add plants to containers.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="font-pixel text-ac-brown text-sm">Care Schedule</h2>

      <Section title="Before you plant" color="bg-ac-yellow">
        <Task>Buy potting mix, not garden soil; garden soil compacts in containers.</Task>
        <Task>Choose pots with drainage holes.</Task>
        <Task>Check your last frost date for {regionLabel} before planting annuals outdoors.</Task>
        {hasBalcony && <Task>Check balcony weight limits; wet soil can get heavy fast.</Task>}
        <Task>Fill pots to within 2 inches of the rim and water thoroughly to settle soil.</Task>
      </Section>

      <Section title="Planting day" color="bg-ac-green text-ac-cream">
        <Task>Place the tallest or most structural plants first.</Task>
        <Task>Add fillers around the center plant, then tuck trailing plants near the rim.</Task>
        <Task>Water deeply until water drains from the bottom.</Task>
        <Task>Move containers into the light conditions you selected for each container.</Task>
      </Section>

      <Section title="Weekly checks" color="bg-ac-pink">
        <Task>Check soil moisture daily in hot weather; containers dry faster than in-ground gardens.</Task>
        {allPlants.map(plant => plant && (
          <Task key={plant.id}>
            <strong>{plant.name}</strong>: {plant.wateringFrequency}
          </Task>
        ))}
        <Task>Inspect leaf undersides and stems for pests.</Task>
      </Section>

      {(perennials.length > 0 || annuals.length > 0) && (
        <Section title="End of season" color="bg-ac-purple">
          {annuals.length > 0 && (
            <Task><strong>Annuals to compost:</strong> {annuals.map(plant => plant!.name).join(', ')}</Task>
          )}
          {perennials.map(plant => plant && (
            <Task key={plant.id}>
              <strong>{plant.name}</strong>: {plant.overwinteringNote}
            </Task>
          ))}
          <Task>Clean empty pots before winter storage.</Task>
        </Section>
      )}

      <p className="font-body text-xs text-ac-brown-light text-center italic">
        Container guidance is conservative. Check the individual plant card when a source-backed note is available.
      </p>
    </div>
  )
}

function Section({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  return (
    <div className="ac-card overflow-hidden p-0">
      <div className={`px-4 py-2 ${color}`}>
        <h3 className="font-body font-extrabold text-sm">{title}</h3>
      </div>
      <ul className="px-4 py-3 space-y-2">{children}</ul>
    </div>
  )
}

function Task({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2 font-body text-sm text-ac-brown">
      <span className="mt-0.5 shrink-0">[ ]</span>
      <span>{children}</span>
    </li>
  )
}
